import { useState, useEffect, useRef, useCallback } from "react";
import { _base_barchart_ui_configs } from "../_base_barchart_ui_configs";

// Minimum number of bars to always show, even in tiny containers
const MIN_VISIBLE_BARS = 3;

// Debounce delay for resize handling (ms)
const RESIZE_DEBOUNCE_MS = 200;

// Height per single bar section (same calculation as TOT_HEIGHT_SINGLE_BAR_SECTION)
const HEIGHT_PER_BAR =
  _base_barchart_ui_configs.barHeight +
  _base_barchart_ui_configs.labelToBarGap +
  _base_barchart_ui_configs.labelHeight +
  _base_barchart_ui_configs.labelToNextBarGap;

// Gap between bar sections
const GAP_BETWEEN_BARS = _base_barchart_ui_configs.gapBetweenBarSections;

// Chart offset from top (same calculation as CHART_OFFSET_TOP)
const CHART_OFFSET_TOP =
  _base_barchart_ui_configs.labelToBarGap +
  _base_barchart_ui_configs.labelHeight +
  (_base_barchart_ui_configs.labelToNextBarGap / 2 - 6);

// Height of the "Others" summarized bar section
const OTHERS_BAR_HEIGHT = HEIGHT_PER_BAR;

interface UseAutoFitBarCountParams {
  /** Ref to the container element to measure */
  containerRef: React.RefObject<HTMLElement>;
  /** Total number of data items available */
  totalDataCount: number;
  /** Whether auto-fit is enabled (typically when summarizeAfterIdx is undefined) */
  enabled: boolean;
}

interface UseAutoFitBarCountResult {
  /** Calculated index after which bars should be summarized, or undefined if all fit */
  calculatedSummarizeAfterIdx: number | undefined;
  /** The measured container height (for debugging) */
  containerHeight: number;
}

/**
 * Hook to automatically calculate how many bars can fit in a container
 * based on the container's height.
 * 
 * This hook:
 * - Measures the container height using ResizeObserver
 * - Calculates the maximum number of bars that can fit
 * - Returns the index after which bars should be summarized
 * - Uses debounced resize handling for performance
 * - Applies a minimum bars constraint (MIN_VISIBLE_BARS)
 */
/**
 * Pure function to calculate summarizeAfterIdx from a known container height.
 * Local to the hook below — report widgets summarise from their own "Combine
 * bars" config instead, so nothing outside measures height to decide this.
 */
const calculateSummarizeIdx = (
  containerHeight: number,
  totalDataCount: number
): number | undefined => {
  if (containerHeight <= 0 || totalDataCount <= 0) return undefined;

  const availableHeightWithOthers = containerHeight - CHART_OFFSET_TOP - OTHERS_BAR_HEIGHT;
  const availableHeightWithoutOthers = containerHeight - CHART_OFFSET_TOP;
  const effectiveHeightPerBar = HEIGHT_PER_BAR + GAP_BETWEEN_BARS;
  const maxBarsWithOthers = Math.floor(availableHeightWithOthers / effectiveHeightPerBar);
  const maxBarsWithoutOthers = Math.floor(availableHeightWithoutOthers / effectiveHeightPerBar);
  const constrainedMaxBars = Math.max(MIN_VISIBLE_BARS, maxBarsWithOthers);

  if (totalDataCount <= maxBarsWithoutOthers) return undefined;
  return constrainedMaxBars - 1;
};

export const useDynamicSummarizeAfterIdx = ({
  containerRef,
  totalDataCount,
  enabled,
}: UseAutoFitBarCountParams): UseAutoFitBarCountResult => {
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced height update function
  const updateHeight = useCallback((newHeight: number) => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounced update
    debounceTimerRef.current = setTimeout(() => {
      setContainerHeight((prevHeight) => {
        // Only update if height changed meaningfully (avoid micro-changes)
        if (Math.abs(prevHeight - newHeight) > 2) {
          return newHeight;
        }
        return prevHeight;
      });
    }, RESIZE_DEBOUNCE_MS);
  }, []);

  // Set up ResizeObserver to track container height
  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        updateHeight(newHeight);
      }
    });

    // Get initial height
    const initialHeight = container.getBoundingClientRect().height;
    setContainerHeight(initialHeight);

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [containerRef, enabled, updateHeight]);

  // Delegate to the pure function so the hook and direct callers stay in sync.
  // While disabled or pre-measure (height === 0), return undefined.
  const calculatedSummarizeAfterIdx =
    !enabled || containerHeight === 0
      ? undefined
      : calculateSummarizeIdx(containerHeight, totalDataCount);

  return {
    calculatedSummarizeAfterIdx,
    containerHeight,
  };
};
